import * as tokensJson from "../tokens.json";

type TokenFile = {
  fontFamily: { sans: string; display: string };
  fontSize: Record<string, string>;
  lineHeight: Record<string, string>;
  radius: Record<string, string>;
  palette: {
    neutral: Record<string, string>;
    brand: Record<string, string>;
    premium: Record<string, string>;
  };
  themes: {
    light: { colors: Record<string, string>; shadow: Record<string, string> };
    dark: { colors: Record<string, string>; shadow: Record<string, string> };
  };
};

const raw = tokensJson as unknown as TokenFile;

export const THEME_STORAGE_KEY = "toolkit-theme";
export const THEME_PREFERENCES = ["light", "dark", "system"] as const;
export const DEFAULT_THEME_PREFERENCE = "light" as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ColorScheme = "light" | "dark";
export type SemanticColors = {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  muted: string;
  mutedForeground: string;
  border: string;
  input: string;
  ring: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;
  success: string;
  successForeground: string;
  destructive: string;
  destructiveForeground: string;
  trust: string;
  trustForeground: string;
};

export type SemanticColorName = keyof SemanticColors;

export const fontFamily = raw.fontFamily;
export const fontSize = raw.fontSize;
export const lineHeight = raw.lineHeight;
export const radius = raw.radius;
export const palette = raw.palette;

export const themes: Record<ColorScheme, SemanticColors> = {
  light: raw.themes.light.colors as SemanticColors,
  dark: raw.themes.dark.colors as SemanticColors,
};

export const shadows = {
  light: raw.themes.light.shadow,
  dark: raw.themes.dark.shadow,
};

export const PRIMARY_HEX = themes.light.primary;

export function cssVarName(colorName: string): string {
  return colorName.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
}

export function themeRgbVars(colors: SemanticColors): Record<string, string> {
  const variables: Record<string, string> = {};
  for (const [key, hex] of Object.entries(colors)) {
    variables[`--tk-${cssVarName(key)}-rgb`] = hexToRgbChannels(hex);
  }
  return variables;
}

export function hexToRgbChannels(hex: string): string {
  const rawHex = hex.replace("#", "");
  const full =
    rawHex.length === 3
      ? rawHex
          .split("")
          .map((char) => char + char)
          .join("")
      : rawHex;
  const numeric = Number.parseInt(full, 16);
  return `${(numeric >> 16) & 255} ${(numeric >> 8) & 255} ${numeric & 255}`;
}

export function colorAlpha(hex: string, alpha: number): string {
  const [r = "0", g = "0", b = "0"] = hexToRgbChannels(hex).split(" ");
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function resolveColorScheme(
  preference: ThemePreference,
  systemScheme: ColorScheme = "light",
): ColorScheme {
  if (preference === "system") {
    return systemScheme;
  }
  return preference;
}

export function isThemePreference(
  value: string | null | undefined,
): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}
